import { Box, Grid, Link, Typography } from '@mui/material';
import { useHttp } from 'hooks/use-fetch';
import WaypointTrackingSection from 'pages/Dashboard/WaypointTrackingSection';
import AppleHealthActivitySummaryChart, {
  DataKey,
} from 'pages/Dashboard/components/AppleHealthActivitySummaryChart';
import AppleHealthChartsWrapper from 'pages/Dashboard/components/AppleHealthChartsWrapper';
import LoggedMoods from 'pages/Dashboard/components/LoggedMoods';
import MoodCard from 'pages/Dashboard/components/MoodCardV2';
import { PatientCtx } from 'pages/Dashboard/constants';
import { fetchPatientWaypointList } from 'pages/Dashboard/services/waypoints.services';
import {
  MoodV2 } from 'pages/Dashboard/types/moods.types';
import {
  WaypointTrackingAPIResponse,
  WaypointType,
} from 'pages/Dashboard/types/waypoints.types';
import {
  TimeFrameOptions,
  getChartDateRanges,
  getFilteredWaypointsData,
  waypointBarSizeMap } from 'pages/Dashboard/utils/trackingUtils';
import * as React from 'react';
import { useQuery } from 'react-query';
import { useParams } from 'react-router-dom';
import theme from 'theme';
import useMoods from 'pages/Dashboard/useMoods';
import useStroopData from 'pages/Dashboard/useStroopData';
import useSomaticScoreData from 'pages/Dashboard/useSomaticScoreData';
import { wayPointsUrls, whoIam } from 'utils/apiUrls';
import HLDrawer from 'components/HLDrawer';
import { TimeFrameCategory } from 'pages/Dashboard/enums';
import SleepComponent from 'pages/Dashboard/components/SleepComponent';
import useSleepData from 'pages/Dashboard/useSleepData';
import HLCard from 'components/HLCard';
import LoadingGif from 'assets/Loading.gif';
import { ReactComponent as NoDataSVG } from 'assets/NoDataFromLogs.svg';
import { PatientObj } from 'pages/Dashboard/types/patient.types';
import ActivityComponent from 'pages/Dashboard/components/ActivityComponent';
import useActivityData from 'pages/Dashboard/useActivityData';
import useWaypointData from 'pages/Dashboard/useWaypointData';
import RatingScaleCard from 'pages/Dashboard/components/RatingScaleCard';
import { fetchWhoIamCategories } from 'pages/Dashboard/services/whoiam.services';
import { CategoryType } from 'pages/Dashboard/types/whoiam.types';
import { PatientWaypoint } from 'pages/Dashboard/types/waypointSuggestion.types';


// Utility to count unique logs by day or month for a given time series and time frame
function countUniqueLogsByTimeFrame(
  timeSeriesData: { timestamp: number }[],
  timeFrame: string,
): number {
  const uniqueKeys = new Set<string>();
  timeSeriesData.forEach((entry) => {
    const date = new Date(entry.timestamp * 1000);
    let key = '';
    if (
      [TimeFrameOptions.LAST_7_DAYS, TimeFrameOptions.LAST_30_DAYS].includes(
          timeFrame as TimeFrameOptions,
      )
    ) {
      // Use YYYY-MM-DD for day uniqueness
      [key] = date.toISOString().split('T');
    }
    else {
      key = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;
    }
    uniqueKeys.add(key);
  });
  return uniqueKeys.size;
}

// Empty state card
export function ProgressMonitorEmptyState() {
  return (
    <HLCard sx={{ height: 520, p: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
      <Box sx={{ mb: 4 }}>
        <NoDataSVG />
      </Box>
      <Typography variant='body1' color='text.primary' fontWeight={700} align='center' sx={{ mb: 1 }}>
        No activity during this time period.
      </Typography>
      <Typography variant='body2' color='text.secondary' align='center' sx={{ mb: 2 }}>
        Try selecting a different time-frame.
      </Typography>
      <Typography variant='body2' color='text.secondary' align='center'>
        You can also nudge your patient during their
        next appointment to use the Headlamp mobile app.
      </Typography>
    </HLCard>
  );
}

type Waypoint = {
  id: string;
  title?: string;
  subTitle?: string;
};

const stroopInfoText = 'The Stroop test is a neuropsychological test that measures how well a person can focus on one thing while ignoring others.';
const somaticScoreText = 'The Habit score reflects how well the patient is doing with things like sleep, daylight exposure, and exercise — areas they can control to improve their overall mental well-being. The score is calculated using Apple Health data.';
interface IProgressMonitorProps {
  timeFrame: string;
  onChangeTimeFrame: (v: string) => void;
  currentWaypointData: WaypointTrackingAPIResponse;
  previousWaypointData: WaypointTrackingAPIResponse;
  patient?: PatientObj;
  setFetchedData: () => void;
}

export default function ProgressMonitor({
  timeFrame: globalTimeFrame,
  onChangeTimeFrame,
  currentWaypointData,
  previousWaypointData,
  patient: patientObj,
  setFetchedData,
}: IProgressMonitorProps) {
  const patient = React.useContext(PatientCtx);
  const { http, providerId, isNewVersionEnabled } = useHttp();
  const { id: patientId } = useParams();
  const [moodId, setMoodId] = React.useState('');
  const [showHealthHabitDetails, setShowHealthHabitDetails] = React.useState(false);
  const [timeFrameMap, setTimeFrameMap] = React.useState<Record<TimeFrameCategory, string>>(
    {} as Record<TimeFrameCategory, string>,
  );

  const orderedCardsRef = React.useRef<string[] | null>(null);

  React.useEffect(() => {
    const defaultTimeFrameMapValues = Object.keys(TimeFrameCategory).reduce((acc, category) => {
      acc[category as TimeFrameCategory] = globalTimeFrame;
      return acc;
    }, {} as Record<TimeFrameCategory, string>);
    setTimeFrameMap(defaultTimeFrameMapValues);
  }, [globalTimeFrame]);

  // Use the custom hook for most logged metrics
  const {
    mostLoggedMetrics,
    hasMostLoggedMetrics,
    moodChartEnergy,
    moodChartPleasantness,
    uniqueMoodsLogged,
    isLoading: isMoodLoading,
  } = useMoods({
    providerId,
    patientId,
    selectedTimeFrame: {
      [TimeFrameCategory.MOOD]: timeFrameMap[TimeFrameCategory.MOOD],
      [TimeFrameCategory.ENERGY]: timeFrameMap[TimeFrameCategory.ENERGY],
      [TimeFrameCategory.MOOD_FACTORS]: timeFrameMap[TimeFrameCategory.MOOD_FACTORS],
    },
    moodId,
  });

  // Use the custom hook for Stroop data
  const {
    stroopTimeSeriesData,
    stroopTimeSeriesAvgData,
    isStroopTimeSeriesDataLoading,
  } = useStroopData({
    providerId,
    patientId,
    timeFrame: timeFrameMap[TimeFrameCategory.COGNITIVE_PERFORMANCE],
  });

  const checkIfTimeFrameInMonths = React.useCallback((timeFrame: string) => (
    [TimeFrameOptions.LAST_3_MONTHS, TimeFrameOptions.LAST_6_MONTHS]
      .includes(timeFrame as TimeFrameOptions)
  ), []);

  const uniqueStroopLogs = React.useMemo(() => {
    const timeFrame = timeFrameMap[TimeFrameCategory.COGNITIVE_PERFORMANCE];
    return checkIfTimeFrameInMonths(timeFrame)
      ? undefined
      : countUniqueLogsByTimeFrame(stroopTimeSeriesData, timeFrame);
  }, [checkIfTimeFrameInMonths, stroopTimeSeriesData, timeFrameMap]);

  // Use the custom hook for Somatic Score data
  const {
    somaticScoreTimeSeriesData,
    somaticScoreTimeSeriesAvgData,
    isSomaticScoresDataLoading,
  } = useSomaticScoreData({
    providerId,
    patientId,
    timeFrame: timeFrameMap[TimeFrameCategory.SOMATIC_SCORE],
    isNewVersionEnabled,
  });

  const uniqueSomaticScoreLogs = React.useMemo(() => {
    const timeFrame = timeFrameMap[TimeFrameCategory.SOMATIC_SCORE];
    return checkIfTimeFrameInMonths(timeFrame)
      ? undefined
      : countUniqueLogsByTimeFrame(somaticScoreTimeSeriesData, timeFrame);
  }, [checkIfTimeFrameInMonths, somaticScoreTimeSeriesData, timeFrameMap]);

  const fetchPatientWaypointsResult = useQuery(
    [`${wayPointsUrls.listPatientWaypoints.queryUrl}`, patientId],
    {
      queryFn: fetchPatientWaypointList(http.get),
      enabled: !!providerId && !!patientId,
      staleTime: 5 * 60 * 1000, // 5 minutes
    },
  );

  const groupedAllWaypoints = getFilteredWaypointsData(
    currentWaypointData.waypoints,
    previousWaypointData.waypoints,
  );

  const getYAxisProps = (timeFrameCategory: TimeFrameCategory) => getChartDateRanges(
    timeFrameMap[timeFrameCategory] as TimeFrameOptions,
  ) as {
    startTime: number;
    endTime: number;
    ticks: number[];
    isInDays: boolean;
  };

  const updateTimeFrames = (newTimeFrame: string, category?: TimeFrameCategory) => {
    if (category) {
      setTimeFrameMap((prev) => ({
        ...prev,
        [category]: newTimeFrame,
      }));
    }
    else {
      onChangeTimeFrame(newTimeFrame);
    }
  };

  React.useEffect(() => {
    // Reset ordered cards ref to recalculate on next render
    if (globalTimeFrame) orderedCardsRef.current = null;
  }, [globalTimeFrame]);

  // Extract Apple Health waypoints and map them by type for direct access
  const {
    sleepWaypoint,
    activityWaypoint,
    daylightExposureWaypoint,
    stepsWaypoint,
  } = React.useMemo(() => {
    const waypoints = fetchPatientWaypointsResult?.data?.waypoints || [];
    const result = {} as Record<string, Waypoint>;

    waypoints.forEach((w: PatientWaypoint) => {
      if (
        w?.waypoint?.type === WaypointType.WAYPOINT_TYPE_DERIVED
        || w?.waypoint?.type === WaypointType.WAYPOINT_TYPE_APPLE_HEALTH
      ) {
        const waypointObj = {
          id: w?.waypoint?.id,
          title: w?.waypoint?.displayName,
          subTitle: w?.waypoint?.description,
        };

        // Map by known types for direct access
        switch (w?.waypoint?.name) {
          case 'sleepDerived':
          case 'sleepAppleHealth':
            if (!result.sleepWaypoint) result.sleepWaypoint = waypointObj;
            break;
          case 'activityDerived':
          case 'activityAppleHealth':
            if (!result.activityWaypoint) result.activityWaypoint = waypointObj;
            break;
          case 'daylightExposure':
            if (!result.daylightExposureWaypoint) result.daylightExposureWaypoint = waypointObj;
            break;
          case 'stepCountDerived':
            if (!result.stepsWaypoint) result.stepsWaypoint = waypointObj;
            break;
          default:
            break;
        }
      }
    });

    return result;
  }, [fetchPatientWaypointsResult?.data?.waypoints]);

  // Sleep
  const sleepData = useSleepData(sleepWaypoint ? {
    providerId,
    waypointId: sleepWaypoint.id,
    patientId: patientId ?? '',
    timeFrame: timeFrameMap[TimeFrameCategory.SLEEP],
  } : { providerId: '', waypointId: '', patientId: '', timeFrame: '' });


  // Activity
  const activityData = useActivityData(activityWaypoint ? {
    patientId: patientId ?? '',
    providerId,
    waypointId: activityWaypoint.id,
    timeFrame: timeFrameMap[TimeFrameCategory.ACTIVITY] as TimeFrameOptions,
  } : { patientId: '', providerId: '', waypointId: '', timeFrame: '' });


  // Daylight Exposure
  const daylightExposureData = useWaypointData(daylightExposureWaypoint ? {
    providerId,
    waypointId: daylightExposureWaypoint.id,
    patientId: patientId ?? '',
    timeFrame: timeFrameMap[TimeFrameCategory.OTHER] as TimeFrameOptions,
  } : {
    providerId: '',
    waypointId: '',
    patientId: '',
    timeFrame: '',
  });

  // Steps
  const stepsData = useWaypointData(stepsWaypoint ? {
    providerId,
    waypointId: stepsWaypoint.id,
    patientId: patientId ?? '',
    timeFrame: timeFrameMap[TimeFrameCategory.STEPS] as TimeFrameOptions,
  } : {
    providerId: '',
    waypointId: '',
    patientId: '',
    timeFrame: '',
  });

  const categoriesData = useQuery([whoIam.categories.queryUrl, providerId, patientId], {
    queryFn: fetchWhoIamCategories(http.get),
    staleTime: 5 * 60 * 1000, // 5 minutes
  });

  const ratingScales = React.useMemo(
    () => categoriesData?.data?.categories?.filter?.(
      (category) => category.type === CategoryType.WIM_CATEGORY_TYPE_RATING_SCALE
        && category.isAttempted
      ,
    ),
    [categoriesData],
  );


  const uniquePleasantnessLogs = React.useMemo(() => {
    const uniqueDates = new Set<string>();
    (moodChartPleasantness?.moods || []).forEach((entry) => {
      uniqueDates.add(`${entry.date.day}-${entry.date.month}-${entry.date.year}`);
    });
    return uniqueDates.size;
  }, [moodChartPleasantness]);

  const uniqueEnergyLogs = React.useMemo(() => {
    const uniqueDates = new Set<string>();
    (moodChartEnergy?.moods || []).forEach((entry) => {
      uniqueDates.add(`${entry.date.day}-${entry.date.month}-${entry.date.year}`);
    });
    return uniqueDates.size;
  }, [moodChartEnergy]);

  const isLoadingOverallData = React.useMemo(
    () => isMoodLoading[MoodV2.MOOD_PLEASANTNESS]
      || isMoodLoading[MoodV2.MOOD_ENERGY]
      || isStroopTimeSeriesDataLoading
      || isSomaticScoresDataLoading
      || fetchPatientWaypointsResult.isLoading
      || sleepData.isLoading
      || activityData.isLoading
      || daylightExposureData.isLoading
      || stepsData.isLoading
      || categoriesData.isLoading,
    [
      isMoodLoading,
      isStroopTimeSeriesDataLoading,
      isSomaticScoresDataLoading,
      fetchPatientWaypointsResult.isLoading,
      sleepData.isLoading,
      activityData.isLoading,
      daylightExposureData.isLoading,
      stepsData.isLoading,
      categoriesData.isLoading,
    ],
  );

  React.useEffect(() => {
    if (!isLoadingOverallData) {
      setFetchedData();
    }
  }, [isLoadingOverallData, setFetchedData]);

  const hasOverallData = React.useMemo(
    () => moodChartEnergy?.moods?.length > 0
      || moodChartPleasantness?.moods?.length > 0
      || stroopTimeSeriesData.length > 0
      || somaticScoreTimeSeriesData.length > 0
      || stepsData.hasData === true
      || activityData.hasData === true
      || sleepData.hasData === true
      || daylightExposureData.hasData === true
      || !!ratingScales?.length,
    [
      moodChartEnergy?.moods?.length,
      moodChartPleasantness?.moods?.length,
      stroopTimeSeriesData.length,
      somaticScoreTimeSeriesData.length,
      stepsData.hasData,
      activityData.hasData,
      sleepData.hasData,
      daylightExposureData.hasData,
      ratingScales,
    ],
  );

  const cardConfigs: Record<
    string,
    {
      hasData: boolean | undefined;
      render: (gridSize?: number) => JSX.Element | null;
      shouldRender?: boolean;
    }
  > = {
    Mood: {
      hasData: moodChartPleasantness?.moods?.length > 0,
      shouldRender: true,
      render: (gridSize = 6) => (
        <MoodCard
          timeFrame={timeFrameMap[TimeFrameCategory.MOOD]}
          cardData={{ label: 'Mood', mood: MoodV2.MOOD_PLEASANTNESS }}
          chartData={moodChartPleasantness?.moods || []}
          noData={moodChartPleasantness?.moods?.length === 0}
          isMoodLoading={isMoodLoading[MoodV2.MOOD_PLEASANTNESS]}
          onTimeFrameChange={(time) => updateTimeFrames(time, TimeFrameCategory.MOOD)}
          gridSize={gridSize}
          numberOfLogs={uniquePleasantnessLogs}
        />
      ),
    },
    MoodFactors: {
      hasData: hasMostLoggedMetrics,
      shouldRender: true,
      render: (gridSize = 6) => (
        <LoggedMoods
          category={TimeFrameCategory.MOOD_FACTORS}
          timeFrame={timeFrameMap[TimeFrameCategory.MOOD_FACTORS]}
          data={mostLoggedMetrics}
          noData={!hasMostLoggedMetrics}
          onChangeTimeFrame={updateTimeFrames}
          onSelectMood={setMoodId}
          gridSize={gridSize}
          numberOfLogs={uniqueMoodsLogged}
        />
      ),
    },
    Energy: {
      hasData: moodChartEnergy?.moods?.length > 0,
      shouldRender: true,
      render: (gridSize = 6) => (
        <MoodCard
          timeFrame={timeFrameMap[TimeFrameCategory.ENERGY]}
          cardData={{ label: 'Energy', mood: MoodV2.MOOD_ENERGY }}
          chartData={moodChartEnergy?.moods || []}
          noData={moodChartEnergy?.moods?.length === 0}
          isMoodLoading={isMoodLoading[MoodV2.MOOD_ENERGY]}
          onTimeFrameChange={(time) => updateTimeFrames(time, TimeFrameCategory.ENERGY)}
          gridSize={gridSize}
          numberOfLogs={uniqueEnergyLogs}
        />
      ),
    },
    Sleep: {
      hasData: sleepData.hasData,
      shouldRender: !!sleepWaypoint,
      render: (gridSize = 6) => {
        if (!sleepWaypoint) return null;
        return (
          <SleepComponent
            timeFrame={timeFrameMap[TimeFrameCategory.SLEEP] as TimeFrameOptions}
            patientId={patientId ?? ''}
            title={sleepWaypoint.title ?? ''}
            key={sleepWaypoint.id}
            onChangeTimeFrame={(v) => updateTimeFrames(v, TimeFrameCategory.SLEEP)}
            averages={sleepData.averages}
            previousPeriodAverages={sleepData.previousPeriodAverages}
            sourceOptions={sleepData.sourceOptions}
            sourceId={sleepData.sourceId}
            onChangeSource={sleepData.onChangeSource}
            sourceObject={sleepData.sourceObject}
            isLoading={sleepData.isWaypointsLoading}
            fetchPatientWaypointTrackingResult={sleepData.fetchPatientWaypointTrackingResult}
            hasData={sleepData.hasData}
            gridSize={gridSize}
          />
        );
      },
    },
    CognitivePerformance: {
      hasData: stroopTimeSeriesData.length > 0,
      shouldRender: true,
      render: (gridSize = 6) => (
        <AppleHealthChartsWrapper
          title='Cognitive performance'
          isLoading={isStroopTimeSeriesDataLoading}
          timeFrame={timeFrameMap[TimeFrameCategory.COGNITIVE_PERFORMANCE]}
          onTimeFrameChange={(v) => updateTimeFrames(v, TimeFrameCategory.COGNITIVE_PERFORMANCE)}
          noData={stroopTimeSeriesData.length === 0}
          infoIcon
          isAppleHealthWaypoint={false}
          numberOfLogs={uniqueStroopLogs}
          avgScore={Math.ceil(stroopTimeSeriesAvgData).toString()}
          gridSize={gridSize}
          infoIconText={(
            <Box>
              <Typography mb={1.5} variant='caption' color='text.primary' component='p'>
                {stroopInfoText}
              </Typography>
              <Link
                underline='hover'
                target='_blank'
                href='https://www.ncbi.nlm.nih.gov/pmc/articles/PMC5388755'
                fontSize={12}
                color={theme.custom.colors.primaryMain}
                fontWeight='normal'
              >
                Learn more
              </Link>
            </Box>
          )}
          renderChart={(
            <AppleHealthActivitySummaryChart
              chartData={stroopTimeSeriesData}
              dataKey={DataKey.Score}
              units=''
              yAxisProps={getYAxisProps(TimeFrameCategory.COGNITIVE_PERFORMANCE)}
              yAxisDefinition='Score'
              showAverages={false}
              barSize={
                waypointBarSizeMap[
                  `${
                    timeFrameMap[TimeFrameCategory.COGNITIVE_PERFORMANCE] as TimeFrameOptions
                  }-${6}`
                ]
              }
              sourceOptions={[]}
              sourceId=''
            />
          )}
        />
      ),
    },
    DaylightExposure: {
      hasData: daylightExposureData.hasData,
      shouldRender: !!daylightExposureWaypoint && daylightExposureData.hasData,
      render: (gridSize = 6) => {
        if (!(!!daylightExposureWaypoint && daylightExposureData.hasData)) return null;
        return (
          <AppleHealthChartsWrapper
            title={daylightExposureWaypoint.title ?? 'Daylight exposure'}
            timeFrame={timeFrameMap[TimeFrameCategory.OTHER] as TimeFrameOptions}
            onTimeFrameChange={(v) => updateTimeFrames(v, TimeFrameCategory.OTHER)}
            key={daylightExposureWaypoint.id}
            sourceObject={daylightExposureData.sourceObject}
            gridSize={gridSize}
            renderChart={(
              <AppleHealthActivitySummaryChart
                chartData={daylightExposureData.chartData}
                dataKey={DataKey.DaylightExposure}
                units={daylightExposureData.absoluteValueUnit}
                yAxisProps={daylightExposureData.yAxisProps}
                yAxisDefinition='Daylight Exposure'
                showAverages={false}
                barSize={waypointBarSizeMap[`${timeFrameMap[TimeFrameCategory.OTHER]}-6`]}
                sourceOptions={daylightExposureData.sourceOptions}
                sourceId={daylightExposureData.sourceId}
                setSourceId={daylightExposureData.onChangeSource}
              />
            )}
          />
        );
      },
    },
    Activity: {
      hasData: activityData.hasData,
      shouldRender: !!activityWaypoint && activityData.hasData,
      render: (gridSize = 6) => {
        if (!(!!activityWaypoint && activityData.hasData)) return null;
        return (
          <ActivityComponent
            timeFrame={timeFrameMap[TimeFrameCategory.ACTIVITY] as TimeFrameOptions}
            key={activityWaypoint.id}
            onChangeTimeFrame={(v: string) => updateTimeFrames(v, TimeFrameCategory.ACTIVITY)}
            appleHealthActivityData={activityData.appleHealthActivityData}
            appleHealthActivityGoals={activityData.appleHealthActivityGoals}
            appleHealthActivityAvgData={activityData.appleHealthActivityAvgData}
            sourceOptions={activityData.sourceOptions}
            sourceId={activityData.sourceId}
            onChangeSource={activityData.onChangeSource}
            yAxisProps={activityData.yAxisProps}
            hasStandHours={activityData.hasStandHours}
            sourceObject={activityData.sourceObject}
            isLoading={activityData.isLoading}
            hasData={activityData.hasData ?? false}
            gridSize={gridSize}
          />
        );
      },
    },
    SomaticScore: {
      hasData: somaticScoreTimeSeriesData.length > 0,
      shouldRender: true,
      render: (gridSize = 6) => (
        <AppleHealthChartsWrapper
          title='Habit score'
          timeFrame={timeFrameMap[TimeFrameCategory.SOMATIC_SCORE]}
          onTimeFrameChange={(v) => updateTimeFrames(v, TimeFrameCategory.SOMATIC_SCORE)}
          isLoading={isSomaticScoresDataLoading}
          noData={somaticScoreTimeSeriesData.length === 0}
          infoIcon
          isAppleHealthWaypoint={false}
          avgScore={Math.ceil(somaticScoreTimeSeriesAvgData).toString()}
          numberOfLogs={uniqueSomaticScoreLogs}
          showViewDetails
          gridSize={gridSize}
          infoIconText={(
            <Box>
              <Typography mb={2} variant='caption' color='text.primary'>
                {somaticScoreText}
              </Typography>
            </Box>
          )}
          renderChart={(
            <AppleHealthActivitySummaryChart
              chartData={somaticScoreTimeSeriesData}
              dataKey={DataKey.SomaticScore}
              units=''
              yAxisProps={getYAxisProps(TimeFrameCategory.SOMATIC_SCORE)}
              yAxisDefinition='Score'
              showAverages={false}
              barSize={
                waypointBarSizeMap[
                  `${timeFrameMap[TimeFrameCategory.SOMATIC_SCORE] as TimeFrameOptions}-${6}`
                ]
              }
              barColor={theme.custom.colors.lightTertiaryMain}
              sourceOptions={[]}
              sourceId=''
            />
          )}
          onViewDetails={() => {
            setShowHealthHabitDetails(true);
          }}
        />
      ),
    },
    Steps: {
      hasData: stepsData.hasData,
      shouldRender: !!stepsWaypoint && stepsData.hasData,
      render: (gridSize = 6) => {
        if (!(!!stepsWaypoint && stepsData.hasData)) return null;
        return (
          <AppleHealthChartsWrapper
            title={stepsWaypoint.title ?? 'Steps'}
            timeFrame={timeFrameMap[TimeFrameCategory.STEPS] as TimeFrameOptions}
            onTimeFrameChange={(v) => updateTimeFrames(v, TimeFrameCategory.STEPS)}
            key={stepsWaypoint.id}
            sourceObject={stepsData.sourceObject}
            renderChart={(
              <AppleHealthActivitySummaryChart
                chartData={stepsData.chartData}
                dataKey={DataKey.Steps}
                units='steps'
                yAxisProps={stepsData.yAxisProps}
                yAxisDefinition='Steps'
                showAverages={false}
                barSize={waypointBarSizeMap[`${timeFrameMap[TimeFrameCategory.STEPS]}-6`]}
                sourceOptions={stepsData.sourceOptions}
                sourceId={stepsData.sourceId}
                setSourceId={stepsData.onChangeSource}
              />
            )}
            gridSize={gridSize}
          />
        );
      },
    },
    RatingScale: {
      hasData: !!ratingScales?.length,
      shouldRender: !!ratingScales?.length,
      render: (gridSize = 12) => (ratingScales?.length ? (
        <RatingScaleCard
          patient={patientObj}
          isLoadingScales={categoriesData.isLoading}
          ratingScales={ratingScales || []}
          timeFrame={timeFrameMap[TimeFrameCategory.RATING_SCALES]}
          onChangeTimeFrame={(v) => updateTimeFrames(v, TimeFrameCategory.RATING_SCALES)}
          gridSize={gridSize}
        />
      ) : null),
    },
  };

  // Determine grid size for each card before RatingScale
  // Calculate orderedCards and gridSizes in a single pass
  // Use refs to persist across renders, only update on first render or when globalTimeFrame changes
  const gridSizesRef = React.useRef<Record<string, 6 | 12>>({});
  if (orderedCardsRef.current === null && !isLoadingOverallData) {
    // 1. Find which cards have data and which don't
    const cardOrderList = Object.keys(cardConfigs);
    const dataAvailable: string[] = [];
    const dataNotAvailable: string[] = [];
    cardOrderList.forEach((key) => {
      if (cardConfigs[key].hasData && cardConfigs[key].shouldRender !== false) {
        dataAvailable.push(key);
      }
      else if (cardConfigs[key].shouldRender !== false) {
        dataNotAvailable.push(key);
      }
    });

    // 2. Order both arrays by cardConfigs keys (already done above)
    // 3. Combine
    const orderedCards = [...dataAvailable, ...dataNotAvailable];
    // 4. Default all to 6 except RatingScale (12)
    const tempGridSizes: Record<string, 6 | 12> = {};
    orderedCards.forEach((key) => {
      tempGridSizes[key] = key === 'RatingScale' ? 12 : 6;
    });
    // 5. If RatingScale exists and odd number before it, set previous card to 12
    const ratingScaleIdx = orderedCards.findIndex((key) => key === 'RatingScale');
    if (ratingScaleIdx > 0) {
      const cardsBefore = orderedCards.slice(0, ratingScaleIdx);
      if (cardsBefore.length % 2 === 1 && cardsBefore.length > 0) {
        tempGridSizes[cardsBefore[cardsBefore.length - 1]] = 12;
      }
    }
    // 6. If RatingScale exists and odd number after, set last after to 12
    if (ratingScaleIdx > -1) {
      const cardsAfter = orderedCards.slice(ratingScaleIdx + 1);
      if (cardsAfter.length % 2 === 1 && cardsAfter.length > 0) {
        tempGridSizes[cardsAfter[cardsAfter.length - 1]] = 12;
      }
    }
    // 7. If no RatingScale, odd total, set last to 12
    if (ratingScaleIdx === -1 && orderedCards.length % 2 === 1 && orderedCards.length > 0) {
      tempGridSizes[orderedCards[orderedCards.length - 1]] = 12;
    }
    orderedCardsRef.current = orderedCards;
    gridSizesRef.current = tempGridSizes;
  }

  let content;
  if (isLoadingOverallData && !orderedCardsRef.current) {
    content = (
      <HLCard sx={{ height: 520, p: 1.5, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center' }}>
          <img src={LoadingGif} alt='Loading...' style={{ width: 300, height: 74 }} />
        </Box>
      </HLCard>
    );
  }
  else if (hasOverallData) {
    content = (
      <Grid container spacing={2} mb={3}>
        {orderedCardsRef.current?.map((key) => (
          <React.Fragment key={key}>
            {cardConfigs[key].render(gridSizesRef.current[key] || 6)}
          </React.Fragment>
        ))}
      </Grid>
    );
  }
  else {
    content = (
      <ProgressMonitorEmptyState />
    );
  }

  return (
    <Box>
      {content}
      <HLDrawer open={showHealthHabitDetails} onClose={() => setShowHealthHabitDetails(false)} title='Habit score details' maxWidth={788}>
        <WaypointTrackingSection
          patient={patient}
          timeFrame={globalTimeFrame}
          currentWaypoints={currentWaypointData?.waypoints}
          previousWaypoints={previousWaypointData?.waypoints}
          allWaypoints={groupedAllWaypoints}
        />
      </HLDrawer>
    </Box>
  );
}
